import { Button, FormLabel, Grow, Popover, Stack, TextField, Typography } from '@mui/material';
import { datadogRum } from '@datadog/browser-rum';
import { ChangeEvent, useEffect, useState } from 'react';

import CloseIcon from '../Icons/CloseIcon';

import TrustPilotCsatSurvey from './TrustPilotCsatSurvey';
import TrustPilotPrompt from './TrustPilotPrompt';
import CustomerSatScoreScale from './CustomerSatScoreScale';
import { CustomerSatSurveyWrapper, FormButtonWrapper, StyledFormControl } from './StyledSurveyComponents';
import { useTrustPilot } from './hooks/useTrustPilot';

import { sendDatadogRumError } from 'utils/analytics/datadogRum';
import { useLayoutContext } from 'modules/common/components/Layout/LayoutContext';
import { bottomRightPopupSession } from 'modules/common/components/Layout/LayoutContext/bottomRightPopupSession';
import useEventTracking from 'modules/common/components/AnalyticsTracking/useEventTracking';
import {
    useRequestSurveyIdAndQuestions,
    useSkipSurvey,
    useSubmitSurveyAnswers,
} from 'modules/common/components/CSatSurvey/query-hooks';
import CheckmarkLoadingAnimated from 'modules/common/components/Icons/CheckmarkLoadingAnimated';
import useAuthSelectors from 'modules/Auth/AuthProvider/useAuthSelector';

type SurveyAnswers = {
    score?: string;
    comment?: string;
};

export default function CSatSurvey({
    open,
    surveyType,
    featureName,
    featureSection,
    otherProperties = {},
    onSurveyComplete,
}: {
    open: boolean;
    surveyType: string;
    featureName: string;
    featureSection: string;
    otherProperties?: Record<string, unknown>;
    /** Called when survey is submitted, skipped, or closed. Use for cleanup. */
    onSurveyComplete?: () => void;
}) {
    const [isSurveyOpen, setSurveyOpen] = useState(open);
    const [answers, setAnswers] = useState<SurveyAnswers>({});
    const [currentOrder, setCurrentOrder] = useState(1);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [skipped, setIsSkipped] = useState(false);

    const { sendTrackingEvent } = useEventTracking();
    const { activeBottomRightPopup, registerBottomRightPopup, unregisterBottomRightPopup } = useLayoutContext();
    const { isImpersonating } = useAuthSelectors('isImpersonating');

    const {
        shouldLoadTrustPilot,
        shouldShowTrustPilot,
        shouldShowTrustPilotPrompt,
        isTrustPilotPromptVisible,
        setIsTrustPilotPromptVisible,
        showTrustpilotPrompt,
        handleScoreChanged,
        handleScoreSubmitted,
        handleTrustPilotPromptContinue,
    } = useTrustPilot({ featureName, featureSection });

    const {
        isSuccess,
        isLoading,
        data: surveyData,
        refetch,
    } = useRequestSurveyIdAndQuestions(surveyType, featureName, featureSection, otherProperties, !isImpersonating);

    const isBlockedByOtherPopup = activeBottomRightPopup !== null && activeBottomRightPopup !== 'csat';

    useEffect(() => {
        if (isSurveyOpen && isSuccess && surveyData) {
            if (isBlockedByOtherPopup) {
                datadogRum.addAction('csat_survey_blocked', {
                    reason: 'other_popup_active',
                    activePopup: activeBottomRightPopup,
                    surveyType,
                    featureName,
                });
            } else {
                registerBottomRightPopup('csat');
            }
        }
        return () => unregisterBottomRightPopup('csat');
    }, [isSurveyOpen, isSuccess, isBlockedByOtherPopup, registerBottomRightPopup, unregisterBottomRightPopup]);

    const { mutateAsync: submitSurveyAnswers } = useSubmitSurveyAnswers();
    const { mutateAsync: skipSurvey } = useSkipSurvey();

    useEffect(() => {
        if (isSurveyOpen && isSuccess && !isBlockedByOtherPopup) {
            bottomRightPopupSession.markAsDisplayed('csat');
            sendTrackingEvent('CSAT Survey Viewed', {
                category: 'CSAT Survey',
                action: 'viewed',
                surveyType,
                featureName,
                featureSection,
            });
        }
    }, [isSurveyOpen, isSuccess, isBlockedByOtherPopup]);

    if (!surveyData || !isSuccess || isLoading || isBlockedByOtherPopup) {
        return null;
    }

    const { surveyId, csatRowId, surveyProperties, surveyQuestions } = surveyData;
    const currentQuestion = surveyQuestions.find((q) => q.order === currentOrder);

    if (!currentQuestion) {
        return null;
    }

    const maxQuestion = surveyQuestions.reduce((max, q) => (q.order > max ? q.order : max), 0);
    const minQuestion = surveyQuestions.reduce((min, q) => (q.order < min ? q.order : min), Number.POSITIVE_INFINITY);
    const pickedScore = Number(answers.score ?? 0);
    const shouldShowNextButton = currentOrder < maxQuestion && currentQuestion.type !== 'score';
    const shouldShowPreviousButton = currentOrder > minQuestion && currentQuestion.type !== 'textField';

    const shouldRenderSurveyQuestions = !shouldShowTrustPilot && !shouldShowTrustPilotPrompt;
    const answer = answers[currentQuestion.id as keyof SurveyAnswers];

    const handleSkipSurvey = async () => {
        await skipSurvey(surveyId);
        setIsSkipped(true);
    };

    const closeSurveyModal = (reason: string) => {
        if (['backdropClick', 'escapeKeyDown'].includes(reason)) {
            return;
        }

        // If survey hasn't been submitted and user is closing, skip it
        if (!isSubmitted && reason !== 'surveySubmitted') {
            // Optimistically skip the survey but still catch if an error happens
            handleSkipSurvey()
                .then(() => refetch())
                .catch((error) => {
                    sendDatadogRumError('CSAT survey skip error', error, {
                        type: 'CSAT_SURVEY_SKIP_ERROR',
                        surveyId,
                        surveyType,
                    });
                });
            sendTrackingEvent('CSAT Survey Closed', {
                category: 'CSAT Survey',
                action: 'close',
                reason,
                surveyType,
                featureName,
                featureSection,
            });
        } else {
            refetch();
        }
        unregisterBottomRightPopup('csat');
        setSurveyOpen(false);
        if (onSurveyComplete) {
            onSurveyComplete();
        }
    };

    const submitSurveyData = async (immediate = false) => {
        if (isSubmitted) {
            return;
        }
        setIsSubmitted(true);

        const postObject = {
            csatRowId,
            surveyType,
            surveyId,
            surveyProperties,
            score: answers.score ?? '',
            comment: answers.comment ?? '',
        };

        const submitAndTrack = async () => {
            await submitSurveyAnswers(postObject);
            sendTrackingEvent('CSAT Survey Submitted', {
                category: 'CSAT Survey',
                action: 'submit',
                surveyType,
                featureName,
                featureSection,
                score: pickedScore,
            });
        };

        try {
            if (immediate) {
                // For immediate submission (when user closes with X),
                // we don't await the promise to avoid blocking the UI
                submitAndTrack().catch((error) => {
                    sendDatadogRumError('CSAT survey submission error', error, {
                        type: 'CSAT_SURVEY_SUBMISSION_ERROR',
                        surveyId,
                        surveyType,
                        featureName,
                        featureSection,
                    });
                });
            } else {
                // For normal submission, await the promise
                await submitAndTrack();
            }
        } catch (error) {
            sendDatadogRumError('CSAT survey submission error', error, {
                type: 'CSAT_SURVEY_SUBMISSION_ERROR',
                surveyId,
                surveyType,
                featureName,
                featureSection,
                otherProperties,
            });
        }
    };

    const handleSubmitSurvey = async (closeSurvey = true, immediate = false) => {
        await submitSurveyData(immediate);

        if (closeSurvey) {
            if (immediate) {
                // For immediate submission, close right away
                closeSurveyModal('surveySubmitted');
            } else {
                // For normal submission, wait 3 seconds then close
                setTimeout(() => {
                    closeSurveyModal('surveySubmitted');
                }, 3000);
            }
        }
    };

    const handleCloseSurvey = (_: unknown, reason: string) => {
        if (['backdropClick', 'escapeKeyDown'].includes(reason)) {
            return;
        }

        const shouldRenderTrustpilotTab = shouldLoadTrustPilot && !isTrustPilotPromptVisible;

        if ((pickedScore || 0) > 0 && reason !== 'surveySubmitted' && !shouldRenderTrustpilotTab) {
            submitSurveyData(true);
            setSurveyOpen(false);
            if (onSurveyComplete) {
                onSurveyComplete();
            }
            return;
        }

        closeSurveyModal(reason);
    };

    const goToNextQuestion = () => {
        if (currentQuestion.type === 'score' && !answer) {
            return;
        }

        if (currentQuestion.type === 'score') {
            handleScoreSubmitted(Number(answer));
        }
        setCurrentOrder((prevState) => Math.min(prevState + 1, maxQuestion));
    };

    const goToPreviousQuestion = () => {
        setCurrentOrder((prevState) => Math.max(prevState - 1, minQuestion));
    };

    const handleAnswerChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (currentQuestion.type === 'score') {
            handleScoreChanged(Number(e.target.value));
        }
        setAnswers((prevAnswers) => ({
            ...prevAnswers,
            [currentQuestion.id]: e.target.value,
        }));
    };

    if (skipped) {
        return (
            <Popover
                slotProps={{
                    paper: {
                        sx: { minWidth: '0px', minHeight: '0px' },
                    },
                }}
                hideBackdrop
                // Click-through (see `pointerEvents` below), so it must not trap focus either.
                disableEnforceFocus
                open={isSurveyOpen}
                onClose={handleCloseSurvey}
                sx={{ pointerEvents: 'none' }}>
                <CustomerSatSurveyWrapper sx={{ pointerEvents: 'auto' }}>
                    <Typography width="90%" textAlign="center" variant="h3">
                        We have skipped this survey for now. You can always take it later.
                    </Typography>
                    <CloseIcon size="small" onClick={() => handleCloseSurvey(null, 'closeButtonClick')} />
                </CustomerSatSurveyWrapper>
            </Popover>
        );
    }

    return (
        <Popover
            slotProps={{
                paper: {
                    sx: {
                        minWidth: '0px',
                        minHeight: '0px',
                    },
                },
            }}
            disableRestoreFocus
            hideBackdrop
            // Click-through (see `pointerEvents` below), so it must not trap focus either.
            disableEnforceFocus
            open={isSurveyOpen}
            onClose={handleCloseSurvey}
            sx={{ pointerEvents: 'none' }}>
            <Grow in={isSurveyOpen} timeout={1000} appear>
                <CustomerSatSurveyWrapper sx={{ pointerEvents: 'auto' }}>
                    {shouldShowTrustPilot && (!showTrustpilotPrompt || !isTrustPilotPromptVisible) && (
                        <TrustPilotCsatSurvey
                            surveyType={surveyType}
                            featureName={featureName}
                            featureSection={featureSection}
                            open={shouldShowTrustPilot && !isTrustPilotPromptVisible}
                            score={Number(pickedScore)}
                            onOpen={() => handleSubmitSurvey(false)}
                        />
                    )}
                    {isTrustPilotPromptVisible && (
                        <TrustPilotPrompt
                            onContinue={() => {
                                handleTrustPilotPromptContinue();
                                setIsTrustPilotPromptVisible(false);
                            }}
                            onClose={() => {
                                setIsTrustPilotPromptVisible(false);
                                handleCloseSurvey(null, 'closeButtonClick');
                            }}
                        />
                    )}
                    {shouldRenderSurveyQuestions && (
                        <>
                            <StyledFormControl>
                                <FormLabel key={currentQuestion.id} id={currentQuestion.id} sx={{ marginBottom: 3 }}>
                                    <Typography variant="h3" marginRight={1}>
                                        {currentQuestion.question}
                                    </Typography>
                                </FormLabel>
                                {currentQuestion.type === 'score' && currentQuestion.scoreSurveyProps ? (
                                    <CustomerSatScoreScale
                                        ariaLabel={currentQuestion.id}
                                        name={currentQuestion.question}
                                        value={String(answer ?? currentQuestion.defaultAnswer)}
                                        onChange={handleAnswerChange}
                                        start={currentQuestion.scoreSurveyProps.start}
                                        end={currentQuestion.scoreSurveyProps.end}
                                    />
                                ) : (
                                    <TextField
                                        sx={{ width: '100%' }}
                                        value={String(answer ?? currentQuestion.defaultAnswer)}
                                        onChange={handleAnswerChange}
                                        placeholder="Write one or two sentences"
                                        aria-label={currentQuestion.question}
                                        label=""
                                        multiline
                                        minRows={2}
                                        slotProps={{
                                            inputLabel: {
                                                shrink: true,
                                            },
                                            htmlInput: {
                                                sx: {
                                                    maxHeight: '70dvh',
                                                },
                                            },
                                        }}
                                        variant="standard"
                                    />
                                )}
                            </StyledFormControl>
                            <FormButtonWrapper>
                                {shouldShowPreviousButton && <Button onClick={goToPreviousQuestion}>Back</Button>}
                                {shouldShowNextButton && (
                                    <Button onClick={goToNextQuestion} disabled={!answer && currentQuestion.required}>
                                        Next
                                    </Button>
                                )}
                                {currentQuestion.type === 'score' && (
                                    <Button variant="contained" fullWidth onClick={goToNextQuestion}>
                                        Confirm
                                    </Button>
                                )}
                                {maxQuestion === currentOrder && (
                                    <Stack direction="column" gap={2} sx={{ width: 1 }}>
                                        {isSubmitted && (
                                            <Typography variant="body1" color="text.secondary">
                                                Thank you for your feedback!
                                            </Typography>
                                        )}
                                        <Button
                                            color={isSubmitted ? 'success' : 'primary'}
                                            variant="contained"
                                            fullWidth
                                            onClick={() => handleSubmitSurvey(true)}>
                                            {isSubmitted ? (
                                                <CheckmarkLoadingAnimated
                                                    slotProps={{
                                                        icon: { sx: { height: 20, color: 'white' } },
                                                        circularProgress: { size: 20, sx: { color: 'white' } },
                                                    }}
                                                />
                                            ) : (
                                                'Submit review'
                                            )}
                                        </Button>
                                    </Stack>
                                )}
                            </FormButtonWrapper>
                        </>
                    )}

                    <CloseIcon size="small" onClick={() => handleCloseSurvey(null, 'closeButtonClick')} />
                </CustomerSatSurveyWrapper>
            </Grow>
        </Popover>
    );
}
